import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type BaseProrrateo = "Peso" | "Valor" | "CBM";

export interface GastoEmbarque {
  id: string;
  embarque_id: string | null;
  org_id: string;
  documento_id: string | null;
  concepto: string;
  monto: number;
  monto_base: number | null;
  impuestos_locales: number | null;
  moneda: string;
  base_prorrateo: BaseProrrateo;
  trm_pago: number | null;
  aplica_costeo_inventario: boolean;
  /** DIAN — true ⇒ suma a Base Gravable (CIF aduanero) para Arancel + IVA. */
  incluir_en_base_gravable: boolean;
  /** B-02A — true ⇒ monto_base se derivó de monto/1.19 por fallo OCR. Requiere revisión humana. */
  alerta_ocr_base: boolean;
  notas: string | null;
  created_at: string;
}

/**
 * Helper canónico: devuelve el monto CAPITALIZABLE al inventario (sin IVA).
 * Si `monto_base` está presente y es > 0, lo usa; si no, cae al `monto`
 * legacy para no romper registros antiguos sin desglose.
 */
export function getMontoCapitalizable(g: { monto: number; monto_base?: number | null }): number {
  const base = Number(g.monto_base ?? 0);
  if (base > 0) return base;
  return Number(g.monto ?? 0);
}

export function useGastosEmbarque(embarqueId: string | null) {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["gastos-embarque", orgId, embarqueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gastos_embarque")
        .select("*")
        .eq("embarque_id", embarqueId!)
        .eq("org_id", orgId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as GastoEmbarque[];
    },
    enabled: !!user && !!orgId && !!embarqueId,
  });
}

export function useCreateGasto() {
  const qc = useQueryClient();
  const { orgId } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      embarque_id: string;
      concepto: string;
      monto: number;
      moneda: string;
      base_prorrateo: BaseProrrateo;
      trm_pago?: number | null;
      aplica_costeo_inventario?: boolean;
      notas?: string;
    }) => {
      if (!orgId) throw new Error("No org context");
      const { error } = await supabase.from("gastos_embarque").insert({
        ...data,
        org_id: orgId,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      // Invalidar por prefijo para hacer match con ["gastos-embarque", orgId, embarqueId]
      qc.invalidateQueries({ queryKey: ["gastos-embarque"] });
      qc.invalidateQueries({ queryKey: ["gastos-huerfanos"] });
      qc.invalidateQueries({ queryKey: ["embarques"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", vars.embarque_id] });
    },
  });
}

export function useUpdateGasto() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      embarque_id: string;
      concepto?: string;
      monto?: number;
      moneda?: string;
      base_prorrateo?: BaseProrrateo;
      trm_pago?: number | null;
      aplica_costeo_inventario?: boolean;
      incluir_en_base_gravable?: boolean;
      notas?: string | null;
    }) => {
      const { id, embarque_id, ...updates } = data;
      const { error } = await supabase
        .from("gastos_embarque")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      // Invalidar por prefijo para refrescar la tabla en EmbarqueDetailModal
      qc.invalidateQueries({ queryKey: ["gastos-embarque"] });
      qc.invalidateQueries({ queryKey: ["gastos-huerfanos"] });
      qc.invalidateQueries({ queryKey: ["embarques"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", vars.embarque_id] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export function useDeleteGasto() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; embarque_id: string }) => {
      const { error } = await supabase
        .from("gastos_embarque")
        .delete()
        .eq("id", data.id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["gastos-embarque"] });
      qc.invalidateQueries({ queryKey: ["gastos-huerfanos"] });
      qc.invalidateQueries({ queryKey: ["embarques"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", vars.embarque_id] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

/** RPC to liquidate and close a shipment definitively */
export function useLiquidarEmbarque() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (embarqueId: string) => {
      const { data, error } = await supabase.rpc("liquidar_y_cerrar_embarque", {
        p_embarque_id: embarqueId,
      });
      if (error) throw error;
      return data as {
        items_liquidados: number;
        trm_aduana_usada: number;
        fob_total_cop: number;
        peso_total_kg: number;
        gastos_peso_cop: number;
        gastos_valor_cop: number;
      };
    },
    onSuccess: (_, embarqueId) => {
      qc.invalidateQueries({ queryKey: ["embarques"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", embarqueId] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["gastos-embarque", embarqueId] });
    },
  });
}

/**
 * Freeze liquidation = persist an immutable snapshot via `commit_landed_cost`.
 * The RPC also runs `liquidar_y_cerrar_embarque` internally, so the embarque
 * is marked as `liquidado = true` and the per-item costs are frozen for
 * customs auditing.
 */
/**
 * Excepción firmada que se persiste de forma inmutable en
 * `excepciones_liquidacion` cuando el usuario decide liquidar a pesar de
 * un mismatch de reconciliación de pesos (Barrera 1).
 */
export interface LiquidationExcepcion {
  motivo: string;
  delta_kg: number;
  delta_pct: number;
  header_kg: number;
  items_kg: number;
  source: string;
}

export function useFreezeLiquidation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      embarqueId: string;
      snapshot: Record<string, any>;
      resolutions?: Record<string, any>;
      /** Si se envía, el RPC registra una excepción firmada vinculada al snapshot. */
      excepcion?: LiquidationExcepcion | null;
    }) => {
      const { data, error } = await (supabase as any).rpc("commit_landed_cost", {
        p_embarque_id: args.embarqueId,
        p_snapshot: args.snapshot,
        p_resolutions: args.resolutions ?? {},
        p_excepcion: args.excepcion ?? null,
      });
      if (error) throw error;
      return data as {
        success: boolean;
        snapshot_id?: string;
        items_liquidados?: number;
        message?: string;
      };
    },
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ["embarques"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", args.embarqueId] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["gastos-embarque", args.embarqueId] });
      qc.invalidateQueries({ queryKey: ["expense-conflicts", args.embarqueId] });
      qc.invalidateQueries({ queryKey: ["landed-cost-snapshots", args.embarqueId] });
    },
  });
}
